import React, { useState } from 'react';
import { Lock, Key, ShieldCheck, AlertCircle } from 'lucide-react';

const Security = ({ userData }: { userData?: any }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null, message: string }>({ type: null, message: '' });
  const [loading, setLoading] = useState(false);

  if (!userData) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });

    if (!currentPassword || !newPassword) {
      setStatus({ type: 'error', message: 'Please fill in both password fields.' });
      return;
    }

    setLoading(true);

    try {
      // NOTE: '/api' ke aage ka route apne express setup ke hisaab se adjust kar lena
      // Example: '/api/customer', '/api/users' ya jo bhi base path aapne define kiya ho
      const response = await fetch(`https://motilalalgo.com/api/profile/${userData.id}/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: 'Password updated successfully!' });
        setCurrentPassword('');
        setNewPassword('');
      } else {
        setStatus({ type: 'error', message: data.error || 'Failed to update password.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-8 font-[Urbanist]">
      <div>
        <h1 className="text-3xl md:text-4xl font-black text-[#0f172a]">Security Settings</h1>
        <p className="text-[#64748b] font-medium mt-1 text-sm md:text-base">Update your account password securely.</p>
      </div>

      <div className="bg-white border border-[#f1f5f9] rounded-[30px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-3 mb-8 border-b border-[#f1f5f9] pb-4">
          <div className="w-12 h-12 bg-[#eff6ff] rounded-full flex items-center justify-center border border-[#e0f2fe]">
            <Lock className="text-[#3b82f6]" size={22} strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-xl font-black text-[#0f172a]">Change Password</h3>
            <p className="text-sm font-bold text-[#64748b]">For User ID: {userData.login_id}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Status Message */}
          {status.type && (
            <div className={`p-4 rounded-[16px] flex items-center gap-3 ${
              status.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
            }`}>
              {status.type === 'error' ? <AlertCircle size={20} /> : <ShieldCheck size={20} />}
              <p className="text-sm font-bold">{status.message}</p>
            </div>
          )}

          {/* Current Password */}
          <div>
            <label className="block text-[12px] text-[#94a3b8] uppercase font-bold tracking-widest mb-2">
              Current Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Key className="text-[#94a3b8]" size={18} />
              </div>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#f1f5f9] rounded-[16px] text-[#0f172a] font-bold focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:bg-white transition-all"
                placeholder="Enter current password"
                required
              />
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-[12px] text-[#94a3b8] uppercase font-bold tracking-widest mb-2">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <ShieldCheck className="text-[#94a3b8]" size={18} />
              </div>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-[#f8fafc] border border-[#f1f5f9] rounded-[16px] text-[#0f172a] font-bold focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:bg-white transition-all"
                placeholder="Enter new password"
                required
                minLength={6}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-4 bg-[#0f172a] text-white py-3.5 rounded-[16px] text-sm font-black hover:bg-[#1e293b] active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating Password...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Security;